import { RiCloseLine } from "@remixicon/react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useLocation, useNavigate } from "react-router";
import { useSidebarStore } from "@/stores/SidebarStore";
import { useEffect, useState, useRef } from "react";
import { getMenus, type MenuItem } from "@/components/navigation/routes-menus";
import React from "react";

// Estilos simplificados para mejor rendimiento
const SimplifiedSidebarStyles = () => (
  <style>{`
    .sidebar-simple {
      background: #374151;
      border-right: 1px solid #4b5563;
      color: white;
    }
    .sidebar-scrollbar::-webkit-scrollbar { width: 5px; }
    .sidebar-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .sidebar-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 3px; }
    
    @media (prefers-contrast: high), (prefers-contrast: more), (-ms-high-contrast: active) {
      .sidebar-simple {
        background: #000000 !important;
        border-right: 2px solid #ffffff !important;
        color: #ffffff !important;
      }
    }
  `}</style>
);

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, closeSidebar } = useSidebarStore();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [allMenus, setAllMenus] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [activeParent, setActiveParent] = useState<string | null>(null);

  // Detección móvil simplificada
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setAllMenus(getMenus());
    const handleClickOutside = (e: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        if (!isMobile) {
          setExpandedItems(new Set());
          setIsSidebarExpanded(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile]);

  useEffect(() => {
    const findActiveAndParent = (
      menus: MenuItem[],
      parentId: string | null = null
    ): { activeId: string | null; parentId: string | null } => {
      for (const item of menus) {
        if (item.href === location.pathname) {
          return { activeId: item.id, parentId };
        }
        if (item.children) {
          const result = findActiveAndParent(item.children, item.id);
          if (result.activeId) {
            return result;
          }
        }
      }
      return { activeId: null, parentId: null };
    };

    const result = findActiveAndParent(allMenus);
    setActiveMenu(result.activeId);
    setActiveParent(result.parentId);

    // Antes: auto-expandía el padre en desktop. Ahora solo en móvil para contexto.
    if (result.parentId && result.activeId) {
      if (isMobile) {
        setExpandedItems(new Set([result.parentId]));
        setIsSidebarExpanded(true);
      } else {
        // En escritorio NO auto-expandimos; se mostrará solo iconografía.
        setExpandedItems(new Set());
        setIsSidebarExpanded(false);
      }
    }
  }, [location.pathname, allMenus, isMobile]);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      // Si hay al menos un ítem expandido, expande el sidebar
      setIsSidebarExpanded(next.size > 0 || isMobile);

      return next;
    });
  };

  const handleClick = (item: MenuItem) => {
    if (item.children?.length) {
      toggleExpand(item.id);
    } else if (item.href) {
      setLoading(true);
      setActiveMenu(item.id);
      if (item.href.startsWith("http://") || item.href.startsWith("https://")) {
        window.location.href = item.href; // Redirige a la URL externa
        setTimeout(() => {
          setLoading(false);
        }, 1500);
      } else {
        navigate(item.href); // Navegación interna con React Router
        setLoading(false);
      }

      // No colapsar si es un elemento hijo
      const isChild = allMenus.some((menu) =>
        menu.children?.some((child) => child.id === item.id)
      );
      // Ahora: siempre colapsar en desktop tras ir a un hijo si no hay expansión manual activa
      if (!isMobile) {
        if (!isChild) {
          setExpandedItems(new Set());
          setIsSidebarExpanded(false);
        } else if (isChild && expandedItems.size === 0) {
          // Caso: navegó a un hijo desde icono directo (auto expand previo eliminado) -> mantener colapsado
          setExpandedItems(new Set());
          setIsSidebarExpanded(false);
        }
      }

      if (isMobile) closeSidebar();
      setTimeout(() => setLoading(false), 500);
    }
  };

  const renderMenuItem = (item: MenuItem, level = 0) => (
    <React.Fragment key={item.id + "-" + level}>
      <button
        onClick={() => handleClick(item)}
        disabled={!item.href && !item.children?.length}
        title={item.label}
        className={`
          w-full text-left px-2 py-2.5 rounded-lg flex items-center transition-all duration-200 group
          ${level > 0 ? "pl-5" : "pl-3"}
          ${
            activeMenu === item.id
              ? "bg-blue-500/50 shadow-lg border border-blue-400/40 text-white"
              : activeParent === item.id
              ? "bg-blue-500/20 border border-blue-400/20"
              : "hover:bg-white/10"
          }
          ${
            !item.href && !item.children?.length
              ? "opacity-50 cursor-not-allowed"
              : "hover:text-blue-100"
          }
        `}
      >
        {item.icon && (
          <item.icon
            className={`
              w-5 h-5 transition-colors shrink-0
              ${
                activeMenu === item.id
                  ? "text-blue-100"
                  : activeParent === item.id
                  ? "text-blue-200"
                  : level === 0
                  ? "text-blue-300/90"
                  : "text-gray-300/80 group-hover:text-blue-200"
              }
            `}
          />
        )}
        <span
          className={`
  ml-2 text-sm truncate transition-colors
  ${
    activeMenu === item.id
      ? "text-white font-semibold"
      : activeParent === item.id
      ? "text-blue-100 font-medium"
      : "text-gray-200 group-hover:text-white"
  }
  ${isMobile || isSidebarExpanded || level > 0 ? "inline-block" : "hidden"}
`}
        >
          {item.label}
        </span>
      </button>

      {item.children &&
        item.children.length > 0 &&
        expandedItems.has(item.id) && (
          <div
            className={`
          ml-5 my-1 pl-4 border-l-2 space-y-1 rounded-bl-sm
          ${level === 0 ? "border-blue-500/30" : "border-gray-500/20"}
        `}
          >
            {item.children.map((c) => renderMenuItem(c, level + 1))}
          </div>
        )}
    </React.Fragment>
  );

  // Determinar el ancho del sidebar
  const getSidebarWidth = () => {
    if (isMobile) return "w-64";
    return isSidebarExpanded ? "w-64" : "w-16";
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <SimplifiedSidebarStyles />

      <aside
        ref={sidebarRef}
        className={`
                    sidebar-simple text-white h-full flex flex-col
                    fixed top-[75px] z-50 transform transition-all duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}
                    ${getSidebarWidth()}
                    lg:relative lg:top-0 lg:translate-x-0
                    `}
      >
        <div className="flex justify-between items-center p-4 border-b border-white/10">
          <h3 className="text-lg font-bold hidden">Menú Principal</h3>
          {isMobile && (
            <button
              onClick={closeSidebar}
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Cerrar menú"
            >
              <RiCloseLine className="w-5 h-5 text-gray-200" />
            </button>
          )}
        </div>

        <div className="overflow-y-auto flex-grow space-y-1 p-2 sidebar-scrollbar">
          {allMenus.map((item) => renderMenuItem(item))}
        </div>

        {/* Footer visible solo cuando está expandido */}
        {(isSidebarExpanded || isMobile) && (
          <div className="p-3 border-t border-white/10">
            <p className="text-xs text-center text-gray-400">
              © 2025 {import.meta.env.VITE_EMPRESA}
            </p>
          </div>
        )}
      </aside>
    </>
  );
};
